"use client";

import React from "react";
import { Mail, Phone, MapPin, Loader, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(({
  className,
  ...props
}, ref) => {
  return <input ref={ref} className={`border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex h-10 w-full rounded-md border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm ${className || ""}`} {...props} />;
});
Input.displayName = "Input";
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="2047" className="flex flex-col items-center justify-center py-10 text-center animate-in fade-in zoom-in duration-300">
        <div className="mb-4 rounded-full bg-accent/10 p-3 text-accent dark:bg-accent/20 dark:text-accent">
          <Send className="size-8" />
        </div>
        <h3 className="text-xl font-bold tracking-tight text-foreground font-sans">Quote Request Sent!</h3>
        <p className="mt-2 max-w-xs text-muted-foreground font-sans">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary text-primary hover:bg-primary/5">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid gap-5 md:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-medium">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className="focus-visible:ring-secondary/50" {...field} />
              </FormControl>
              <FormDescription className="sr-only">
                First and last name
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-medium">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="focus-visible:ring-secondary/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-medium">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(02) 4500 0000" className="focus-visible:ring-secondary/50" {...field} />
              </FormControl>
              <FormDescription className="text-xs font-mono">
                For urgent repair updates
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-medium">Caravan Model</FormLabel>
              <FormControl>
                <Input placeholder="e.g., 2023 Jayco Journey" className="focus-visible:ring-secondary/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Details are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans font-medium">Power & Repair Requirements</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your repair needs or solar/electrical upgrade requirements..." className="min-h-[120px] focus-visible:ring-secondary/50" {...field} />
            </FormControl>
            <FormDescription className="font-mono text-[10px] uppercase tracking-wider">
              Include insurance claim numbers if applicable
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive font-sans">
          {submitError}
        </div>}

      <div className="space-y-3">
        <Button type="submit" className="w-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-sans font-bold uppercase tracking-wider transition-all duration-200" disabled={isSubmitting}>
          {isSubmitting ? <>
              <Loader className="mr-2 size-4 animate-spin" />
              Processing...
            </> : "Get a Free Quote"}
        </Button>
        <p className="text-center text-xs font-mono text-muted-foreground uppercase tracking-widest">
          We usually reply within 24 hours
        </p>
      </div>
    </Form>;
}
export default function GetInTouch() {
  return <section id="get-a-free-quote" className="bg-background py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-5xl">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.5fr]">
            {}
            <div className="flex flex-col justify-center space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold tracking-tighter text-foreground sm:text-4xl lg:text-5xl font-sans">
                  Precision Caravan Repairs in South Windsor
                </h2>
                <p className="text-lg text-muted-foreground font-sans leading-relaxed">
                  From major insurance smash repairs to off-grid solar installations, our expert team restores your caravan to optimal condition with industrial-grade precision.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="mt-1 flex size-10 shrink-0 items-center justify-center rounded-md bg-secondary/10 text-secondary">
                    <MapPin className="size-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground font-sans uppercase tracking-tight">Workshop Location</h3>
                    <p className="mt-1 text-muted-foreground font-sans">6/104 Ham St, South Windsor NSW 2756</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="mt-1 flex size-10 shrink-0 items-center justify-center rounded-md bg-secondary/10 text-secondary">
                    <Phone className="size-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground font-sans uppercase tracking-tight">Technical Support</h3>
                    <p className="mt-1 text-muted-foreground font-sans">0401 316 697</p>
                    <p className="text-xs font-mono text-muted-foreground uppercase mt-1">Mon-Fri: 9:00am - 5:00pm Sat: By Appointment; Sun: Closed</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="mt-1 flex size-10 shrink-0 items-center justify-center rounded-md bg-secondary/10 text-secondary">
                    <Mail className="size-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground font-sans uppercase tracking-tight">Direct Inquiry</h3>
                    <p className="mt-1 text-muted-foreground font-sans">caravansmashrepairs@yahoo.com</p>
                  </div>
                </div>
              </div>
            </div>

            {}
            <div className="relative">
              {}
              <div className="absolute -inset-4 -z-10 rounded-[2rem] bg-gradient-to-br from-secondary/5 to-accent/10 blur-2xl" />
              
              <Card className="border-border shadow-2xl rounded-xl backdrop-blur-sm bg-card/95">
                <CardHeader className="space-y-1 pb-6 border-b border-border/50">
                  <CardTitle className="text-2xl font-bold font-sans tracking-tight">Request a Quote</CardTitle>
                  <CardDescription className="font-sans">
                    Provide your details below and our specialists will assess your requirements.
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <FormProvider formKey="free_quote_form_solar_battery_systems_generators" formId="free_quote_form_solar_battery_systems_generators" sectionName="get-a-free-quote" defaultValues={{
                  name: "",
                  email: "",
                  phone: "",
                  subject: "",
                  message: ""
                }} validationRules={{
                  name: {
                    required: true
                  },
                  email: {
                    required: true,
                    pattern: "email"
                  },
                  message: {
                    required: true
                  }
                }} submitText="Get a Free Quote" successMessage="Your request has been received. Our repair specialists will contact you shortly.">
                    <ContactFormContent />
                  </FormProvider>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>;
}